// progress-store.js
import { Storage, KEYS, STORAGE_VERSION } from './storage.js';
import { exKey, exRoundKey, blockKey, idFor } from './utils.js';

/** @typedef {{recoverable: boolean, code: 'read-failed'|'corrupt-json'|'invalid-state'|'storage-unavailable'|'quota-exceeded'|'write-failed'|'remove-failed', operation: 'probe'|'read'|'write'|'remove', key: string|null, cause: unknown|null}} StorageIssue */

/** Returns whether a value is a non-null, non-array object with Object.prototype. @param {unknown} value @returns {boolean} */
export const isPlainObject = (value) =>
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;

/** Returns whether every map value is the completion sentinel `true`. @param {unknown} value @returns {boolean} */
export const isCompletionMap = (value) =>
  isPlainObject(value) && Object.values(value).every((entry) => entry === true);

/** Returns whether every map value is a non-negative completed-round count. @param {unknown} value @returns {boolean} */
export const isBlockRoundsMap = (value) =>
  isPlainObject(value) &&
  Object.values(value).every((entry) => Number.isInteger(entry) && entry >= 0);

/**
 * @typedef {Object} PersistenceOutcome
 * @property {boolean} ok Whether the requested inputs were valid.
 * @property {boolean} changed Whether in-memory state changed.
 * @property {boolean} persisted Whether the current state reached localStorage.
 * @property {unknown} value Operation-specific value.
 * @property {StorageIssue|null} issue Recoverable persistence issue.
 */

/** Builds a consistent result for progress mutations. @param {boolean} changed @param {boolean} persisted @param {unknown} value @param {StorageIssue|null} [issue=null] @param {boolean} [ok=true] @returns {PersistenceOutcome} */
const outcome = (changed, persisted, value, issue = null, ok = true) => ({
  ok,
  changed,
  persisted,
  value,
  issue,
});

const importError = (code, message, details = {}) =>
  Object.assign(new Error(message), { code, ...details });

const invalidOutcome = () => outcome(false, false, null, null, false);

const isIdentifier = (value) => typeof value === 'string' && value.trim().length > 0;

const isRoundIndex = (value) => Number.isInteger(value) && value >= 0;

const isTotalRounds = (value) => Number.isInteger(value) && value > 0;

const isElement = (value) =>
  value !== null && value?.nodeType === 1 && typeof value.querySelectorAll === 'function';

const mapsEqual = (left, right) => {
  const leftKeys = Object.keys(left);
  return (
    leftKeys.length === Object.keys(right).length &&
    leftKeys.every((key) => Object.hasOwn(right, key) && left[key] === right[key])
  );
};

export default class ProgressStore {
  /** Loads validated progress maps and records any existing recovery quarantine. */
  constructor() {
    this._quarantinedKeys = new Set();
    this._quarantineIssues = new Map();
    this._dirtyKeys = new Set();
    this.exCompleted = this._loadMap(KEYS.EX, isCompletionMap);
    this.exRounds = this._loadMap(KEYS.EXR, isCompletionMap);
    this.blockRounds = this._loadMap(KEYS.BLOCK, isBlockRoundsMap);
    Storage.getCorruptEntries().forEach(([key]) => {
      if ([KEYS.EX, KEYS.EXR, KEYS.BLOCK].includes(key)) this._quarantinedKeys.add(key);
    });
  }

  /** @private Tracks a damaged map that must not be silently overwritten. */
  _quarantine(key, issue) {
    this._quarantinedKeys.add(key);
    this._quarantineIssues.set(key, issue);
  }

  /** @private Reads, parses, and validates a map; raw invalid data is preserved and quarantined. */
  _loadMap(key, validator) {
    const raw = Storage.getRaw(key);
    if (raw === null) return {};

    const value = Storage.getJSON(key, {});
    const lastIssue = Storage.getLastIssue();
    if (
      lastIssue?.code === 'corrupt-json' &&
      lastIssue.operation === 'read' &&
      lastIssue.key === key
    ) {
      this._quarantine(key, lastIssue);
      return {};
    }
    if (validator(value)) return value;

    Storage.preserveCorrupt(key, raw);
    const issue = Storage.reportIssue({
      code: 'invalid-state',
      operation: 'read',
      key,
      cause: null,
    });
    this._quarantine(key, issue);
    return {};
  }

  /** @private Validates positive integer week/day inputs before building keys. */
  static _validateParams(week, day) {
    const w = parseInt(week, 10);
    const d = parseInt(day, 10);
    if (isNaN(w) || isNaN(d) || w < 1 || d < 1) {
      console.warn('[ProgressStore] Invalid week/day:', week, day);
      return null;
    }
    return { week: w, day: d };
  }

  /** @private Refuses implicit quarantine replacement and keeps failed maps dirty for retries. */
  _persist(key, value, replaceQuarantined = false) {
    if (this._quarantinedKeys.has(key) && !replaceQuarantined) {
      return outcome(
        false,
        false,
        value,
        this._quarantineIssues.get(key) ?? Storage.getLastIssue()
      );
    }
    const persisted = Storage.setJSON(key, value);
    if (persisted && replaceQuarantined) {
      Storage.clearCorrupt([key]);
      this._quarantinedKeys.delete(key);
      this._quarantineIssues.delete(key);
    }
    if (persisted) this._dirtyKeys.delete(key);
    else this._dirtyKeys.add(key);
    return outcome(false, persisted, value, persisted ? null : Storage.getLastIssue());
  }

  /** Persists legacy current-exercise completion state. @param {boolean} [replaceQuarantined=false] @returns {PersistenceOutcome} */
  persistExercises(replaceQuarantined = false) {
    return this._persist(KEYS.EX, this.exCompleted, replaceQuarantined);
  }

  /** Marks legacy completion state for a later retry. @returns {void} */
  markExercisesDirty() {
    this._dirtyKeys.add(KEYS.EX);
  }

  /** Persists canonical zero-based round completion state. @param {boolean} [replaceQuarantined=false] @returns {PersistenceOutcome} */
  persistExerciseRounds(replaceQuarantined = false) {
    return this._persist(KEYS.EXR, this.exRounds, replaceQuarantined);
  }

  /** Persists completed-round counts when dirty, forced, or explicitly replacing quarantine. @param {boolean} [replaceQuarantined=false] @param {boolean} [force=false] @returns {PersistenceOutcome} */
  persistBlockRounds(replaceQuarantined = false, force = false) {
    if (
      !force &&
      !replaceQuarantined &&
      !this._dirtyKeys.has(KEYS.BLOCK) &&
      !this._quarantinedKeys.has(KEYS.BLOCK)
    ) {
      return outcome(false, true, this.blockRounds);
    }
    return this._persist(KEYS.BLOCK, this.blockRounds, replaceQuarantined);
  }

  /** Marks block-round counts for a later retry. @returns {void} */
  markBlockRoundsDirty() {
    this._dirtyKeys.add(KEYS.BLOCK);
  }

  /** Saves all maps, returning the first persistence failure while attempting each map. @param {{replaceQuarantinedKeys?: Set<string>}} [options] @returns {PersistenceOutcome} */
  save({ replaceQuarantinedKeys = new Set() } = {}) {
    // Save each map so one failed key does not prevent independent dirty data from retrying.
    const results = [
      this.persistExercises(replaceQuarantinedKeys.has(KEYS.EX)),
      this.persistExerciseRounds(replaceQuarantinedKeys.has(KEYS.EXR)),
      this.persistBlockRounds(replaceQuarantinedKeys.has(KEYS.BLOCK), true),
    ];
    const failed = results.find((result) => !result.persisted);
    return outcome(
      false,
      !failed,
      results.map((result) => result.value),
      failed?.issue ?? null
    );
  }

  /** Sets legacy current-exercise completion state. @param {number|string} week @param {number|string} day @param {string} ex @param {boolean} v @returns {PersistenceOutcome} */
  setExercise(week, day, ex, v) {
    const params = ProgressStore._validateParams(week, day);
    if (!params || !isIdentifier(ex)) return invalidOutcome();
    const k = exKey(params.week, params.day, ex);
    const changed = v ? this.exCompleted[k] !== true : Object.hasOwn(this.exCompleted, k);
    if (v) this.exCompleted[k] = true;
    else delete this.exCompleted[k];
    if (changed) this._dirtyKeys.add(KEYS.EX);
    if (!changed && !this._dirtyKeys.has(KEYS.EX)) return outcome(false, true, k);
    const result = this.persistExercises();
    return outcome(changed, result.persisted, k, result.issue);
  }

  /** Sets canonical completion state for one zero-based block round. @param {number|string} week @param {number|string} day @param {string} blockId @param {number} roundIndex @param {string} exId @param {boolean} v @returns {PersistenceOutcome} */
  setExerciseRound(week, day, blockId, roundIndex, exId, v) {
    const params = ProgressStore._validateParams(week, day);
    if (!params || !isIdentifier(blockId) || !isRoundIndex(roundIndex) || !isIdentifier(exId)) {
      return invalidOutcome();
    }
    const k = exRoundKey(params.week, params.day, blockId, roundIndex, exId);
    const changed = v ? this.exRounds[k] !== true : Object.hasOwn(this.exRounds, k);
    if (v) this.exRounds[k] = true;
    else delete this.exRounds[k];
    if (changed) this._dirtyKeys.add(KEYS.EXR);
    if (!changed && !this._dirtyKeys.has(KEYS.EXR)) return outcome(false, true, k);
    const result = this.persistExerciseRounds();
    return outcome(changed, result.persisted, k, result.issue);
  }

  /** Returns the completed-round count clamped to the block's total. @param {number|string} week @param {number|string} day @param {string} blockId @param {number} total @returns {number} */
  getCurrentRound(week, day, blockId, total) {
    return Math.max(0, Math.min(total, this.blockRounds[blockKey(week, day, blockId)] || 0));
  }

  /** Advances one completed-round count, remaining idempotent after the final round. @param {number|string} week @param {number|string} day @param {string} blockId @param {number} total @returns {PersistenceOutcome} */
  advanceRound(week, day, blockId, total) {
    const params = ProgressStore._validateParams(week, day);
    if (!params || !isIdentifier(blockId) || !isTotalRounds(total)) return invalidOutcome();
    const bKey = blockKey(params.week, params.day, blockId);
    const cur = Math.max(0, Math.min(total, this.blockRounds[bKey] || 0));
    if (cur >= total) {
      if (!this._dirtyKeys.has(KEYS.BLOCK)) return outcome(false, true, cur);
      const result = this.persistBlockRounds();
      return outcome(false, result.persisted, cur, result.issue);
    }
    const next = cur + 1;
    this.blockRounds[bKey] = next;
    this._dirtyKeys.add(KEYS.BLOCK);
    const result = this.persistBlockRounds();
    return outcome(true, result.persisted, next, result.issue);
  }

  /** Clears legacy completion state for exercises found in a DOM block. @param {number|string} week @param {number|string} day @param {Element} blockEl @returns {PersistenceOutcome} */
  clearBlockExerciseState(week, day, blockEl) {
    const params = ProgressStore._validateParams(week, day);
    if (!params || !isElement(blockEl)) return invalidOutcome();
    const ids = Array.from(blockEl.querySelectorAll('.exercise-row[data-ex]')).map((el) =>
      el.getAttribute('data-ex')
    );
    let changed = false;
    ids.forEach((ex) => {
      const k = exKey(params.week, params.day, ex);
      if (k in this.exCompleted) {
        delete this.exCompleted[k];
        changed = true;
      }
    });
    if (!changed) {
      if (!this._dirtyKeys.has(KEYS.EX)) return outcome(false, true, null);
      const result = this.persistExercises();
      return outcome(false, result.persisted, null, result.issue);
    }
    this._dirtyKeys.add(KEYS.EX);
    const result = this.persistExercises();
    return outcome(true, result.persisted, null, result.issue);
  }

  /** Resets a block's round history, legacy DOM exercise state, and counter. @param {number|string} week @param {number|string} day @param {string} blockId @param {Element|null} [blockEl=null] @returns {PersistenceOutcome} */
  resetBlock(week, day, blockId, blockEl = null) {
    const params = ProgressStore._validateParams(week, day);
    if (!params || !isIdentifier(blockId) || (blockEl !== null && !isElement(blockEl))) {
      return invalidOutcome();
    }

    const id = idFor(params.week, params.day);
    const blockPrefix = `${id}::${blockId}::`;

    // Prefix matching clears every canonical round key without assuming a known round total.
    let changed = false;
    Object.keys(this.exRounds).forEach((k) => {
      if (k.startsWith(blockPrefix)) {
        delete this.exRounds[k];
        changed = true;
      }
    });

    // Legacy keys lack a block id, so only DOM membership can safely identify them.
    if (blockEl) {
      const exerciseIds = Array.from(blockEl.querySelectorAll('.exercise-row[data-ex]')).map((el) =>
        el.getAttribute('data-ex')
      );
      exerciseIds.forEach((ex) => {
        const k = exKey(params.week, params.day, ex);
        if (Object.hasOwn(this.exCompleted, k)) {
          delete this.exCompleted[k];
          changed = true;
        }
      });
    }

    // Remove the completed-round counter so round zero becomes current again.
    const roundKey = blockKey(params.week, params.day, blockId);
    if (Object.hasOwn(this.blockRounds, roundKey)) {
      delete this.blockRounds[roundKey];
      changed = true;
    }
    const result = this.save();
    return outcome(changed, result.persisted, blockId, result.issue);
  }

  /** Resets all progress maps belonging to one programme day. @param {number|string} week @param {number|string} day @returns {PersistenceOutcome} */
  resetDay(week, day) {
    const params = ProgressStore._validateParams(week, day);
    if (!params) return outcome(false, false, null, null, false);

    const id = idFor(params.week, params.day);
    let changed = false;
    Object.keys(this.exRounds).forEach((k) => {
      if (k.startsWith(`${id}::`)) {
        delete this.exRounds[k];
        changed = true;
      }
    });
    Object.keys(this.exCompleted).forEach((k) => {
      if (k.startsWith(`${id}:`)) {
        delete this.exCompleted[k];
        changed = true;
      }
    });
    Object.keys(this.blockRounds).forEach((k) => {
      if (k.startsWith(`${id}::`)) {
        delete this.blockRounds[k];
        changed = true;
      }
    });
    const result = this.save();
    return outcome(changed, result.persisted, id, result.issue);
  }

  /** Resets all in-memory progress and attempts every relevant storage removal. @returns {PersistenceOutcome} */
  resetAll() {
    const keys = [KEYS.EX, KEYS.EXR, KEYS.BLOCK];
    const hasMemory = [this.exCompleted, this.exRounds, this.blockRounds].some(
      (map) => Object.keys(map).length > 0
    );
    const keysToRemove = keys.filter((key) => {
      const raw = Storage.getRaw(key);
      const issue = Storage.getLastIssue();
      return (
        raw !== null ||
        (issue?.code === 'read-failed' && issue.operation === 'read' && issue.key === key)
      );
    });
    this.exCompleted = {};
    this.exRounds = {};
    this.blockRounds = {};
    // Only successful removals clear dirty/quarantine state; failures remain recoverable.
    const results = keysToRemove.map((key) => ({
      key,
      persisted: Storage.remove(key),
      issue: Storage.getLastIssue(),
    }));
    const failed = results.find((result) => !result.persisted);
    results
      .filter((result) => result.persisted)
      .forEach(({ key }) => {
        this._quarantinedKeys.delete(key);
        this._quarantineIssues.delete(key);
        this._dirtyKeys.delete(key);
      });
    return outcome(
      hasMemory || results.some((result) => result.persisted),
      !failed,
      results,
      failed?.issue ?? null
    );
  }

  /** Returns raw quarantined entries in the damaged-backup format. @returns {{exportedAt: string, entries: Record<string, string>}} */
  getCorruptBackup() {
    return {
      exportedAt: new Date().toISOString(),
      entries: Object.fromEntries(Storage.getCorruptEntries()),
    };
  }

  /** Discards only quarantined progress maps, leaving unrelated corrupt keys untouched. @returns {PersistenceOutcome} */
  discardCorrupt() {
    const progressKeys = new Set([KEYS.EX, KEYS.EXR, KEYS.BLOCK]);
    const entries = Storage.getCorruptEntries().filter(([key]) => progressKeys.has(key));
    if (entries.length === 0) return outcome(false, true, [], null);

    const properties = {
      [KEYS.EX]: 'exCompleted',
      [KEYS.EXR]: 'exRounds',
      [KEYS.BLOCK]: 'blockRounds',
    };
    const results = entries.map(([key]) => {
      const persisted = Storage.remove(key);
      if (persisted && properties[key]) this[properties[key]] = {};
      return { key, persisted, issue: persisted ? null : Storage.getLastIssue() };
    });
    Storage.clearCorrupt(results.filter((result) => result.persisted).map((result) => result.key));
    const failed = results.find((result) => !result.persisted);
    results
      .filter((result) => result.persisted)
      .forEach(({ key }) => {
        this._quarantinedKeys.delete(key);
        this._quarantineIssues.delete(key);
        this._dirtyKeys.delete(key);
      });
    return outcome(
      results.some((result) => result.persisted),
      !failed,
      results,
      failed?.issue ?? null
    );
  }

  /** Downloads `workout-progress.json` with all three maps and version-1 metadata. @returns {void} */
  export() {
    const payload = {
      completedExercises: this.exCompleted,
      completedExerciseRounds: this.exRounds,
      blockRounds: this.blockRounds,
      ts: new Date().toISOString(),
      version: 1,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'workout-progress.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Release the temporary browser URL after the synchronous download click.
    URL.revokeObjectURL(url);
  }

  /**
   * Imports optional validated v1 maps without mutating state until validation succeeds.
   * @param {File} file
   * @returns {Promise<PersistenceOutcome>} Rejects with an Error whose `.code` is `no-file`,
   * `read-failed`, `invalid-json`, `invalid-shape`, `unsupported-version`, or `persist-failed`;
   * the latter also has an `.outcome`.
   */
  import(file) {
    return new Promise((resolve, reject) => {
      let settled = false;
      const rejectRead = () => {
        if (settled) return;
        settled = true;
        reject(importError('read-failed', 'Failed to read file'));
      };
      if (!file) {
        settled = true;
        reject(importError('no-file', 'No file provided'));
        return;
      }

      const reader = new FileReader();

      reader.onload = (e) => {
        if (settled) return;
        let data;
        try {
          data = JSON.parse(e.target.result);
        } catch (err) {
          settled = true;
          reject(importError('invalid-json', 'Failed to parse file: ' + err.message));
          return;
        }

        if (!isPlainObject(data)) {
          settled = true;
          reject(importError('invalid-shape', 'Invalid file format'));
          return;
        }
        if (Object.hasOwn(data, 'version') && data.version !== STORAGE_VERSION) {
          settled = true;
          reject(importError('unsupported-version', 'Unsupported progress file version'));
          return;
        }

        const candidates = [
          ['completedExercises', 'exCompleted', isCompletionMap],
          ['completedExerciseRounds', 'exRounds', isCompletionMap],
          ['blockRounds', 'blockRounds', isBlockRoundsMap],
        ];
        for (const [field, , validator] of candidates) {
          if (Object.hasOwn(data, field) && !validator(data[field])) {
            settled = true;
            reject(importError('invalid-shape', `Invalid ${field} map`));
            return;
          }
        }

        // Validate into staged maps first: invalid input cannot mutate live state, and only
        // explicitly supplied maps may replace their quarantined counterparts.
        const staged = {
          exCompleted: this.exCompleted,
          exRounds: this.exRounds,
          blockRounds: this.blockRounds,
        };
        candidates.forEach(([field, property]) => {
          if (Object.hasOwn(data, field)) staged[property] = data[field];
        });
        const changedKeys = [
          [KEYS.EX, this.exCompleted, staged.exCompleted],
          [KEYS.EXR, this.exRounds, staged.exRounds],
          [KEYS.BLOCK, this.blockRounds, staged.blockRounds],
        ].filter(([, current, next]) => !mapsEqual(current, next));
        const replacementKeys = new Set(
          candidates
            .filter(([field]) => Object.hasOwn(data, field))
            .map(
              ([, property]) =>
                ({
                  exCompleted: KEYS.EX,
                  exRounds: KEYS.EXR,
                  blockRounds: KEYS.BLOCK,
                })[property]
            )
        );
        const changed =
          changedKeys.length > 0 ||
          [...replacementKeys].some((key) => this._quarantinedKeys.has(key));
        const hasDirtyState = [KEYS.EX, KEYS.EXR, KEYS.BLOCK].some((key) =>
          this._dirtyKeys.has(key)
        );
        if (!changed && !hasDirtyState) {
          settled = true;
          resolve(outcome(false, true, data.ts));
          return;
        }
        if (changed) {
          this.exCompleted = staged.exCompleted;
          this.exRounds = staged.exRounds;
          this.blockRounds = staged.blockRounds;
          changedKeys.forEach(([key]) => this._dirtyKeys.add(key));
        }
        const result = this.save({ replaceQuarantinedKeys: replacementKeys });
        if (!result.persisted) {
          settled = true;
          reject(
            importError('persist-failed', 'Imported progress could not be saved', {
              outcome: result,
            })
          );
          return;
        }
        settled = true;
        resolve(outcome(true, true, data.ts));
      };

      reader.onerror = rejectRead;
      reader.onabort = rejectRead;

      try {
        reader.readAsText(file);
      } catch {
        rejectRead();
      }
    });
  }

  /** Counts canonical round completions plus legacy completions without canonical history. @param {number|string} week @param {number|string} day @returns {number} */
  getExDoneForDay(week, day) {
    const prefix = `w${week}d${day}::`;
    const roundCount = Object.keys(this.exRounds).filter(
      (k) => k.startsWith(prefix) && this.exRounds[k]
    ).length;
    const legacyPrefix = `w${week}d${day}:`;
    const legacyCount = Object.keys(this.exCompleted).filter((key) => {
      if (!key.startsWith(legacyPrefix) || key.includes('::') || !this.exCompleted[key]) {
        return false;
      }
      return !this.hasRoundHistoryForExercise(week, day, key.slice(legacyPrefix.length));
    }).length;
    return roundCount + legacyCount;
  }

  /** Returns whether an exercise has any canonical round-history entry for a day. @param {number|string} week @param {number|string} day @param {string} exId @returns {boolean} */
  hasRoundHistoryForExercise(week, day, exId) {
    const prefix = `w${week}d${day}::`;
    const suffix = `:${exId}`;
    return Object.keys(this.exRounds).some(
      (key) => key.startsWith(prefix) && key.endsWith(suffix) && this.exRounds[key]
    );
  }

  /** Counts canonical completed exercise-round entries across every day. @returns {number} */
  getExDoneForAllDays() {
    return Object.keys(this.exRounds).filter((k) => this.exRounds[k]).length;
  }

  /**
   * Answers "where am I in this programme?".
   *
   * Opening the app put you at Week 1 Day 1 -- the beginning of a programme
   * you may be six weeks into -- so every session started with scrolling and
   * remembering. The answer needs no new stored data and no programme JSON on
   * the client: the overview already carries every day's week, number and
   * exercise total on its progress bars, so the caller passes those in and
   * this resolves them against what has actually been marked.
   *
   * A day part-way through wins over an untouched one, because an abandoned
   * day is the thing you are most likely to have meant to come back to.
   *
   * @param {Array<{week: number|string, day: number|string, total: number}>} days
   *   Every day in the programme, in order.
   * @returns {{week: number|string, day: number|string, total: number, done: number,
   *   state: 'in-progress'|'not-started'|'finished'}|null} `null` if given no days.
   */
  resume(days) {
    const list = Array.isArray(days) ? days : [];
    if (list.length === 0) return null;

    const withProgress = list.map((entry) => ({
      ...entry,
      done: this.getExDoneForDay(entry.week, entry.day),
    }));

    const started = withProgress.find((d) => d.done > 0 && d.done < d.total);
    if (started) return { ...started, state: 'in-progress' };

    const untouched = withProgress.find((d) => d.done === 0);
    if (untouched) return { ...untouched, state: 'not-started' };

    // Every day is accounted for, so the programme is done. The last day is
    // the one to offer -- there is nowhere forward to go.
    const last = withProgress[withProgress.length - 1];
    return { ...last, state: 'finished' };
  }

  // Note: Total exercises are driven by the template data.
  // We'll pass them from the UI instances.
}
